import { useEffect, useRef, useState } from 'react';
import { ErrorBox, dash, wallClock, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
import { api } from '../lib/api';
import { canScreen, useMe } from '../lib/me';
import { CommuteCopy, CommuteEditor } from './SettleCommute';
import RecvRoutes from './RecvRoutes';
import DisptDetail from './DisptDetail';

/** 담당 이메일(#369) — 이름 아래 한 줄. 이름 칸이 이미 그 메일이면 다시 쓰지 않는다. */
function MailLine({ mail, same }: { mail: unknown; same: unknown }) {
  const m = mail ? String(mail) : '';
  if (!m || m.toLowerCase() === String(same ?? '').trim().toLowerCase()) return null;
  return <div className="break-all text-xs text-ink-soft">✉ {m}</div>;
}

/**
 * 접수 화면 **오른쪽 패널** — 고른 접수 하나를 세 계층으로 편다.
 *
 *   ① 접수 요약 카드 (왼쪽 목록에서 뺀 상세 필드가 여기 온다)
 *   ② 그 접수의 노선 목록
 *   ③ 고른 노선에 배차된 차량
 *
 * 왼쪽을 2줄 목록으로 줄인 대가를 여기서 갚는다 — 접수일·접수자·청구방법·입찰 조건은
 * 버린 게 아니라 **표 한 칸에서 라벨 붙은 카드로 옮긴** 것이고, 그 편이 읽기 쉽다.
 *
 * ⚠️ ②의 높이를 묶어 둔다(`RecvRoutes` 의 `max-h`). 안 묶으면 노선이 많은 접수에서
 * 목록이 그대로 늘어나 **③ 배차가 화면 밖으로 밀려난다** — 노선을 고를 때마다 맨 아래까지
 * 스크롤해야 했다(2026-07-31 사용자 신고).
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-ink-faint">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export default function RecvPane({
  recv,
  canWrite,
  onEditRecv,
  onAddRoute,
  onCopy,
  onSeeAllRoutes,
  onEditRoute,
  onCopyRoute,
  onChanged,
  onPickRoute,
  nonce,
}: {
  recv: Row;
  canWrite: boolean;
  onEditRecv: (recvId: number) => void;
  onAddRoute: (recvId: number) => void;
  /** 접수 복사 — 안 주면 버튼을 안 낸다(정산 화면의 일반 노선·기사 편집 창 #520). */
  onCopy?: (recv: Row) => void;
  onSeeAllRoutes: (recvId: number, label: string) => void;
  onEditRoute: (recvId: number, disptId: number) => void;
  /** 노선 복사(#178) — 고른 노선 값을 채운 새 노선 등록 창. */
  onCopyRoute?: (recvId: number, disptId: number) => void;
  onChanged: (msg: string) => void;
  /** 고른 노선을 위로 알린다 — 부모가 `Ctrl+C` 로 **그 노선만** 담을 수 있게(2026-08-02). */
  onPickRoute?: (disptId: number | null) => void;
  nonce: number;
}) {
  const recvId = Number(recv.recv_id);
  const [dispt, setDispt] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [delError, setDelError] = useState<string | null>(null);
  const carsRef = useRef<HTMLDivElement>(null);
  const { me } = useMe();
  /**
   * 통근 월별 노선·배차(10-15 본부장님 미팅 「전월 불러오기는 배차 쪽에서 · 접수는 그대로, 노선·배차가 정산연월별로」).
   * 정산의 「노선·기사 편집」과 같은 편집기·같은 「다른 달 그대로 가져오기」를 접수 화면에서 연다(같은 서버·같은 권한 = 정산 화면).
   */
  const isCommute = String(recv.recv_type ?? '') === '통근';
  const canCommuteRead = canScreen(me, 'settle', 'read');
  const canCommuteWrite = canScreen(me, 'settle', 'write');
  const [commuteMon, setCommuteMon] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [commuteOpen, setCommuteOpen] = useState<'edit' | 'copy' | null>(null);

  // 접수를 바꾸면 아래 칸을 비운다 — 안 비우면 **다른 접수의 배차**가 그 자리에 남는다.
  useEffect(() => {
    setDispt(null);
    setDelError(null);
    setCommuteOpen(null);
  }, [recvId]);

  // 접수 삭제(#148) — 노선이 남았거나 정산·청구·계산서가 붙어 있으면 서버가 사유와 함께 거부한다.
  async function removeRecv() {
    if (!window.confirm(`접수 ${recvId}번 「${String(recv.cust_name ?? '')}」을(를) 삭제할까요?`)) return;
    setBusy(true);
    setDelError(null);
    try {
      await api.deleteRecv({ recvId });
      onChanged(`접수 ${recvId}번을 삭제했습니다.`);
    } catch (e) {
      setDelError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // 고른 노선을 부모에게 알린다(복사 단축키가 이걸 본다). 렌더 중이 아니라 **효과에서** 올린다 —
  // 렌더 도중 부모 상태를 바꾸면 React 가 경고하고 한 번 더 그린다.
  useEffect(() => {
    onPickRoute?.(dispt ? Number(dispt.dispt_id) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispt]);

  // 노선을 고르면 배차 칸을 화면 안으로 끌어온다. `nearest` 라 이미 보이면 아무 일도 안 한다
  // (보이는데도 스크롤하면 화면이 튄다).
  useEffect(() => {
    if (dispt) carsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [dispt]);

  return (
    <div className="space-y-3">
      <div className="card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="break-keep text-base font-bold">{dash(recv.cust_name)}</h2>
          {/* 접수 NO = 레거시 RecvId 와 같은 번호(이관 때 그대로, 새 접수도 그 뒤를 잇는다). */}
          <span className="text-xs text-ink-soft">접수 NO {recvId}</span>
          <span className="rounded bg-canvas px-1.5 py-0.5 text-xs font-medium text-ink-soft">
            {dash(recv.recv_type)}
          </span>
          <span className="rounded bg-canvas px-1.5 py-0.5 text-xs font-medium text-ink-soft">
            {dash(recv.status)}
          </span>
          {/* 이 접수에 할 수 있는 일. 회색 잔글씨로 두면 찾아야 한다 —
              가장 자주 쓰는 `노선 추가`만 눈에 띄게 두고 나머지는 보조로 둔다. */}
          {canWrite && (
            <div className="ml-auto flex items-center gap-1.5">
              <button className="btn-primary py-1 text-sm" onClick={() => onAddRoute(recvId)}>
                + 노선 추가
              </button>
              <button className="btn-ghost py-1 text-sm" onClick={() => onEditRecv(recvId)}>
                접수 수정
              </button>
              {onCopy && (
                <button
                  className="btn-ghost py-1 text-sm"
                  title="이 접수를 다음 달 등으로 복사합니다"
                  onClick={() => onCopy(recv)}
                >
                  복사
                </button>
              )}
              <button
                className="btn-ghost py-1 text-sm text-red-600"
                title="노선이 없는 접수만 지울 수 있습니다. 노선이 있으면 노선을 먼저 삭제하세요."
                onClick={removeRecv}
                disabled={busy}
              >
                접수 삭제
              </button>
            </div>
          )}
        </div>
        {delError && (
          <div className="mt-2">
            <ErrorBox message={delError} />
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="접수일">{dash(recv.recv_date)}</Field>
          <Field label="운행 기간">
            {dash(recv.recv_startdt)} ~ {dash(recv.recv_enddt)}
          </Field>
          <Field label="노선수">{dash(recv.dispt_cnt)}</Field>
          {/* 담당 전화(#368) — 거래처 담당자 목록에서 이름·이메일이 같은 줄의 번호. */}
          <Field label="거래처담당자">
            {dash(recv.recv_user)}
            {recv.recv_user_phone ? <span className="ml-2 text-ink-soft">☎ {String(recv.recv_user_phone)}</span> : null}
            <MailLine mail={recv.recv_user_mail} same={recv.recv_user} />
          </Field>
          {/* 담당자는 **누가 이 접수를 보는지**를 정한다. 미지정이면 전원에게 보이므로 그렇게 적는다 —
              빈칸('—')으로 두면 "담당이 없다"인지 "안 보인다"인지 화면에서 구분이 안 된다. */}
          <Field label="버스인접수담당자">
            {recv.owner_app_user_id ? (
              <>
                {dash(recv.owner_user_name ?? recv.owner_user_email)}
                {recv.owner_user_team ? (
                  <span className="ml-1 text-xs text-ink-faint">{String(recv.owner_user_team)}</span>
                ) : null}
              </>
            ) : (
              <span className="text-ink-faint">미지정 · 전원에게 보임</span>
            )}
            {/* 함께 담당(10-19) — 「주담당 · 함께: 이름, 이름」. 함께 담당자도 이 접수를 자기 범위에서 본다. */}
            {recv.co_owner_names ? (
              <span className="ml-1">
                <span className="text-ink-faint">· 함께: </span>
                {String(recv.co_owner_names)}
              </span>
            ) : null}
          </Field>
          {/* 정산담당자(레거시 접수의 「정산담당자」 — 2026-09-30 way 「표기가 안 보인다」). 위 「담당자」(웹에서 지정·보기 범위)와 별개. */}
          <Field label="거래처정산담당자">
            {dash(recv.fin_manager)}
            {recv.fin_manager_phone ? <span className="ml-2 text-ink-soft">☎ {String(recv.fin_manager_phone)}</span> : null}
            <MailLine mail={recv.fin_manager_mail} same={recv.fin_manager} />
          </Field>
          <Field label="청구방법">{dash(recv.fin_req_method)}</Field>
          <Field label="입찰">
            {recv.is_bidding === true ? (
              <>
                {dash(recv.bidding_type)}
                {recv.close_date ? (
                  <span className="ml-1 text-xs text-ink-faint">마감 {wallClock(recv.close_date)}</span>
                ) : null}
              </>
            ) : (
              <span className="text-ink-faint">비공개</span>
            )}
          </Field>
        </div>

        {recv.recv_request ? (
          <div className="mt-3">
            <Field label="요청사항">{String(recv.recv_request)}</Field>
          </div>
        ) : null}

        {isCommute && canCommuteRead && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm">
            <span className="font-semibold text-brand-700">통근 월별 노선·배차</span>
            <input className="field w-36 py-1" type="month" aria-label="정산월" value={commuteMon}
                   onChange={(e) => e.target.value && setCommuteMon(e.target.value)} />
            <button className="btn-ghost py-1 text-sm" onClick={() => setCommuteOpen('edit')}
                    title="그 달 정산에 들어가는 노선·기사·운행수·단가 — 정산의 「노선·기사 편집」과 같은 창">
              {commuteMon} 노선·배차 보기{canCommuteWrite ? '·편집' : ''}
            </button>
            {canCommuteWrite && (
              <button className="btn-primary py-1 text-sm" onClick={() => setCommuteOpen('copy')}
                      title="지난달(또는 고른 달)의 노선·기사·단가·운행수를 이 달로 그대로 옮깁니다 — 옮긴 뒤 편집에서 고치면 됩니다">
                전월 불러오기
              </button>
            )}
          </div>
        )}
      </div>

      {commuteOpen === 'edit' && (
        <Modal width="max-w-6xl">
          <CommuteEditor recvId={recvId} driveMon={commuteMon} canWrite={canCommuteWrite} onClose={() => setCommuteOpen(null)} />
        </Modal>
      )}
      {commuteOpen === 'copy' && (
        <Modal>
          <CommuteCopy recvId={recvId} driveMon={commuteMon} onClose={() => setCommuteOpen(null)}
                       onDone={() => { setCommuteOpen('edit'); onChanged(`${commuteMon} 통근 노선·배차를 불러왔습니다.`); }} />
        </Modal>
      )}

      <RecvRoutes
        recv={recv}
        selectedDisptId={dispt ? Number(dispt.dispt_id) : null}
        onPick={setDispt}
        onSeeAll={(id) => onSeeAllRoutes(id, String(recv.cust_name ?? `접수 ${id}`))}
        nonce={nonce}
      />

      <div ref={carsRef}>
        {dispt ? (
          <DisptDetail
            dispt={dispt}
            onClose={() => setDispt(null)}
            onChanged={onChanged}
            onEdit={() => onEditRoute(recvId, Number(dispt.dispt_id))}
            onCopy={onCopyRoute ? () => onCopyRoute(recvId, Number(dispt.dispt_id)) : undefined}
            onAdd={() => onAddRoute(recvId)}
            onDeleted={(m) => {
              setDispt(null);
              onChanged(m);
            }}
          />
        ) : (
          <div className="card p-4 text-sm text-ink-faint">
            노선을 누르면 그 노선에 <b>배차</b>된 차량이 여기 나옵니다.
          </div>
        )}
      </div>
    </div>
  );
}
